'use client';

import { c as _c } from "react/compiler-runtime";
import { X } from 'lucide-react';
import type { FC } from 'react';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import Button from '../common/Button';
import { usePortalTheme } from '../pages/space/terminalThemes/portal';
const modalStack: Array<() => void> = [];
interface Props {
  children: React.ReactNode;
  style?: React.CSSProperties;
  closeHandler?: () => void;
  maxWidth?: number;
  /** Set false when a stray click outside should NOT dismiss — a prompt the
   *  user has to answer, rather than a panel they are browsing. The X and any
   *  in-content buttons still close it. */
  dismissOnBackdrop?: boolean;
}
const Modal: FC<Props> = t0 => {
  const $ = _c(20);
  const {
    children,
    style: t1,
    closeHandler,
    maxWidth: t2,
    dismissOnBackdrop: t3
  } = t0;
  let t4;
  if ($[0] !== t1) {
    t4 = t1 === undefined ? {
      marginLeft: "20px",
      marginRight: "20px"
    } : t1;
    $[0] = t1;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const style = t4;
  const maxWidth = t2 === undefined ? 700 : t2;
  const dismissOnBackdrop = t3 === undefined ? true : t3;
  let t5;
  if ($[2] !== closeHandler || $[3] !== dismissOnBackdrop) {
    t5 = e => {
      if (e.button !== 0) {
        return;
      }
      const overlay = e.currentTarget;
      if (!overlay.contains(e.target as Node)) {
        return;
      }
      e.stopPropagation();
      if (!dismissOnBackdrop) {
        return;
      }
      closeHandler?.();
    };
    $[2] = closeHandler;
    $[3] = dismissOnBackdrop;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  const handleOverlayMouseDown = t5;
  const portalTheme = usePortalTheme();
  const stop = _temp;
  let t6;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = [];
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  useEffect(_temp2, t6);
  const [mounted, setMounted] = useState(false);
  let t7;
  let t8;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => setMounted(true);
    t8 = [];
    $[6] = t7;
    $[7] = t8;
  } else {
    t7 = $[6];
    t8 = $[7];
  }
  useEffect(t7, t8);
  if (!mounted) {
    return null;
  }
  let t9;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {
      position: "fixed",
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
      width: "100vw",
      height: "100vh"
    };
    $[8] = t9;
  } else {
    t9 = $[8];
  }
  let t10;
  if ($[9] !== maxWidth || $[10] !== portalTheme.style || $[11] !== style) {
    t10 = {
      ...portalTheme.style,
      ...style,
      maxWidth
    };
    $[9] = maxWidth;
    $[10] = portalTheme.style;
    $[11] = style;
    $[12] = t10;
  } else {
    t10 = $[12];
  }
  let t11;
  if ($[13] !== children || $[14] !== portalTheme.attrs || $[15] !== t10) {
    t11 = <div className="flex h-screen items-center justify-center"><div data-testid="modal" role="dialog" aria-modal="true" {...portalTheme.attrs} style={t10} className="flex max-h-[90vh] w-full flex-col overflow-hidden rounded-xl border border-ds-border-standard bg-background-secondary shadow-2xl transition-[max-width] duration-300 ease-out" onClick={stop} onMouseDown={stop} onPointerDown={stop}>{children}</div></div>;
    $[13] = children;
    $[14] = portalTheme.attrs;
    $[15] = t10;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  let t12;
  if ($[17] !== handleOverlayMouseDown || $[18] !== t11) {
    t12 = createPortal(<div className="animate-fadeIn fixed inset-0 z-modal h-screen w-screen bg-black/50" style={t9} onMouseDown={handleOverlayMouseDown} onClick={stop} onMouseUp={stop}>{t11}</div>, document.body);
    $[17] = handleOverlayMouseDown;
    $[18] = t11;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  return t12;
};
export const ModalHeader: FC<{
  children?: React.ReactNode;
  closeHandler?: () => void;
  headerElement?: React.ReactNode;
  flex?: boolean;
  /** Replaces the X when closing means something other than "stop this" —
   *  a deploy, say, which keeps running on the runner after the modal goes. */
  closeIcon?: React.ReactNode;
  /** Accessible name for that control. Defaults to "Close". */
  closeLabel?: string;
}> = t0 => {
  const $ = _c(11);
  const {
    children,
    closeHandler,
    headerElement,
    flex: t1,
    closeIcon,
    closeLabel
  } = t0;
  const flex = t1 === undefined ? true : t1;
  let t2;
  let t3;
  if ($[0] !== closeHandler) {
    t2 = () => {
      if (!closeHandler) {
        return;
      }
      modalStack.push(closeHandler);
      const closeModal = e => {
        if (e.key === "Escape" && modalStack[modalStack.length - 1] === closeHandler) {
          closeHandler();
        }
      };
      document.addEventListener("keydown", closeModal);
      return () => {
        document.removeEventListener("keydown", closeModal);
        const idx = modalStack.indexOf(closeHandler);
        if (idx !== -1) {
          modalStack.splice(idx, 1);
        }
      };
    };
    t3 = [closeHandler];
    $[0] = closeHandler;
    $[1] = t2;
    $[2] = t3;
  } else {
    t2 = $[1];
    t3 = $[2];
  }
  useEffect(t2, t3);
  const t4 = ` ${flex ? "flex" : ""} modal-header shrink-0 items-center justify-between border-b border-solid border-ds-border-subtle p-4 text-lg font-semibold`;
  const t5 = headerElement ? headerElement : children;
  let t6;
  if ($[3] !== closeHandler || $[4] !== closeIcon || $[5] !== closeLabel) {
    t6 = closeHandler && <Button variant="SECONDARY" onClick={() => closeHandler()} ariaLabel={closeLabel ?? "Close"} icon={closeIcon ?? <X size={20} />} />;
    $[3] = closeHandler;
    $[4] = closeIcon;
    $[5] = closeLabel;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] !== t4 || $[8] !== t5 || $[9] !== t6) {
    t7 = <div className={t4}>{t5}{t6}</div>;
    $[7] = t4;
    $[8] = t5;
    $[9] = t6;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  return t7;
};
export const ModalContent: FC<{
  children: React.ReactNode;
  banner?: React.ReactNode;
  // Opt-in for modals whose body content grows/shrinks (e.g. live search
  // results). Holds a stable min-height ON THE SCROLL CONTAINER so the leftover
  // space is slack (no scrollbar) instead of forcing a tall child that
  // overflows — and the modal doesn't bounce as content changes. Off by
  // default so other modals stay sized to their content. Clamped to
  // 90vh-minus-chrome (20rem ≈ header + a tall footer) so header/footer stay
  // visible on short screens.
  fillHeight?: boolean;
  // Stronger than fillHeight: pins the scroller to ONE height, so a modal whose
  // body swaps between screens (a navigation stack) never resizes as you move
  // through it. fillHeight only sets a floor, which still lets a tall screen
  // grow the modal and makes the next one snap back down.
  //
  // 'compact' pins a shorter one. Pick it when the screens people actually see
  // are short and the tall one is the exception: the full height then leaves a
  // block of dead space under every normal screen, and the exception can
  // scroll instead.
  lockHeight?: boolean | 'compact';
}> = t0 => {
  const $ = _c(6);
  const {
    children,
    banner,
    fillHeight,
    lockHeight
  } = t0;
  const t1 = banner && banner;
  const t2 = `modal-content min-h-0 w-full overflow-y-auto overscroll-y-contain p-4 ${lockHeight === "compact" ? "h-[min(24rem,calc(90vh-11rem))]" : lockHeight ? "h-[min(36rem,calc(90vh-11rem))]" : fillHeight ? "min-h-[min(32rem,calc(90vh-20rem))]" : ""}`;
  let t3;
  if ($[0] !== children || $[1] !== t2) {
    t3 = <div className={t2}>{children}</div>;
    $[0] = children;
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== t1 || $[4] !== t3) {
    t4 = <>{t1}{t3}</>;
    $[3] = t1;
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  return t4;
};
export const ModalFooter: FC<{
  children: React.ReactNode;
}> = t0 => {
  const $ = _c(2);
  const {
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <div className="shrink-0 space-x-2 border-t border-solid border-ds-border-subtle p-3 text-center md:text-end laptop:text-end">{children}</div>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};
export default Modal;
function _temp(e_0) {
  return e_0.stopPropagation();
}
function _temp2() {
  const scrollRoot = document.querySelector("[data-scroll-lock-root]") ?? document.body;
  const scrollbarWidth = scrollRoot === document.body ? window.innerWidth - document.documentElement.clientWidth : scrollRoot.offsetWidth - scrollRoot.clientWidth;
  const prevOverflow = scrollRoot.style.overflow;
  const prevPaddingRight = scrollRoot.style.paddingRight;
  scrollRoot.style.overflow = "hidden";
  scrollRoot.style.paddingRight = scrollbarWidth > 0 ? `${scrollbarWidth}px` : "";
  return () => {
    scrollRoot.style.overflow = prevOverflow;
    scrollRoot.style.paddingRight = prevPaddingRight;
  };
}